"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ApolloMeetingModuleT } from "@/types/dataTypes";
import Section from "@/components/furniture/Section";
import Container from "../furniture/Container";
import SectionWrapper from "../furniture/SectionWrapper";
import FormField from "../forms/FormField";
import Button from "../furniture/Button";
import H4 from "../typography/H4";
import P from "../typography/P";
import { typogrify } from "@/utils/string";

interface FormState {
  firstname: string;
  lastname: string;
  company: string;
  email: string;
  phone: string;
}

interface FormErrors {
  firstname?: string;
  lastname?: string;
  company?: string;
  email?: string;
  phone?: string;
}

const INITIAL_FORM_STATE = {
  firstname: "",
  lastname: "",
  company: "",
  email: "",
  phone: "",
};

const ApolloMeetingModule: React.FC<Omit<ApolloMeetingModuleT, "_type">> = ({
  appId,
  schedulingLink,
  title,
  description,
}) => {
  const formElRef = useRef<HTMLDivElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);

  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (isMounted && appId && schedulingLink) {
      const script = document.createElement("script");
      script.src = "https://assets.apollo.io/js/meetings/meetings-widget.js";
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (window.ApolloMeetings && formElRef.current) {
          window.ApolloMeetings.initWidget({
            appId,
            schedulingLink,
            domElement: formElRef.current,
          });
        }
      };

      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [isMounted, appId, schedulingLink]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate first name
    if (!formState.firstname.trim()) {
      newErrors.firstname = "First name is required";
    }

    // Validate last name
    if (!formState.lastname.trim()) {
      newErrors.lastname = "Last name is required";
    }

    // Validate email
    if (!formState.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Validate company
    if (!formState.company.trim()) {
      newErrors.company = "Company is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));

    // Clear error when user types
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Pass the form data to Apollo
      window.ApolloMeetings.submit({
        map: true,
        lead: {
          firstName: formState.firstname,
          lastName: formState.lastname,
          company: formState.company,
          email: formState.email,
          phone: formState.phone,
        },
      });

      setFormState(INITIAL_FORM_STATE);
    } catch (error) {
      console.error("Form submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section id="apollo-meeting-form">
      <Container width="narrower">
        <SectionWrapper>
          <div className="flex flex-col items-center gap-y-8 px-6 py-12 sm:mx-auto sm:p-12 rounded-lg bg-darkCream">
            {!!(!!title || !!description) && (
              <header className="text-center flex flex-col gap-8 max-w-rich-text text-pretty">
                {!!title && <H4 as="h2">{typogrify(title)}</H4>}
                {!!description && <P>{typogrify(description)}</P>}
              </header>
            )}

            <div className="mx-auto w-full">
              <form method="POST" onSubmit={handleFormSubmit} noValidate>
                <div className="flex flex-col gap-y-4 sm:gap-y-8">
                  <FormField
                    label="First name"
                    name="firstname"
                    required
                    value={formState.firstname}
                    onChange={handleInputChange}
                    error={errors.firstname}
                  />

                  <FormField
                    label="Last name"
                    name="lastname"
                    required
                    value={formState.lastname}
                    onChange={handleInputChange}
                    error={errors.lastname}
                  />

                  <FormField
                    label="Company"
                    name="company"
                    required
                    value={formState.company}
                    onChange={handleInputChange}
                    error={errors.company}
                  />

                  <FormField
                    label="Email"
                    name="email"
                    type="email"
                    required
                    value={formState.email}
                    onChange={handleInputChange}
                    error={errors.email}
                  />

                  <FormField
                    label="Telephone"
                    name="phone"
                    value={formState.phone}
                    onChange={handleInputChange}
                    error={errors.phone}
                  />

                  <div className="flex justify-end pt-4">
                    <Button
                      className={`button button--dark w-full sm:w-auto ${isSubmitting ? "opacity-75" : ""}`}
                      type="submit"
                      disabled={isSubmitting}
                      asButton
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </Button>
                  </div>
                </div>
              </form>

              {isMounted &&
                createPortal(
                  <div
                    ref={formElRef}
                    className="[&>div>div]:fixed [&>div>div]:inset-0"
                  />,
                  document.body,
                )}
            </div>

          </div>
        </SectionWrapper>
      </Container>
    </Section>
  );
};

export default ApolloMeetingModule;
